import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Calendar,
  Clock,
  User,
  ChatCircleText,
  CheckCircle
} from '@phosphor-icons/react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enGB } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

const BookingForm = ({ businessId, serviceTypeId, onBookingSuccess, onClose }) => {
  const styles = useThemeStyles();
  const { _user } = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    businessId: businessId || '',
    serviceTypeId: serviceTypeId || '',
    startTime: '',
    endTime: '',
    clientNotes: ''
  });

  // Available data
  const [business, setBusiness] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');

  const parseLocalDateString = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const formatDateToLocalString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const steps = [
    'Select Appointment',
    'Choose Date & Time',
    'Add Details',
    'Confirm Booking'
  ];

  useEffect(() => {
    if (businessId) {
      setFormData(prev => ({ ...prev, businessId }));
      fetchBusinessDetails();
      fetchServiceTypes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId]);

  useEffect(() => {
    if (formData.serviceTypeId) {
      const serviceType = serviceTypes.find(st => st._id === formData.serviceTypeId);
      setSelectedServiceType(serviceType);
    }
  }, [formData.serviceTypeId, serviceTypes]);

  useEffect(() => {
    if (selectedDate && formData.serviceTypeId && formData.businessId) {
      fetchAvailableSlots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedDate, formData.serviceTypeId, formData.businessId]);


  const fetchBusinessDetails = async () => {
    try {
      // Use the public supplier endpoint to get business details
      const response = await api.get(`/api/supplier/${businessId}`);
      setBusiness(response.data);
    } catch (error) {
      setError('Failed to load business details');
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const response = await api.get(`/api/service-types/business/${businessId}/public`);
      setServiceTypes(response.data.serviceTypes);

      // If serviceTypeId is provided, set it
      if (serviceTypeId && response.data.serviceTypes.length > 0) {
        const serviceType = response.data.serviceTypes.find(st => st._id === serviceTypeId);
        if (serviceType) {
          setFormData(prev => ({ ...prev, serviceTypeId: serviceTypeId }));
        }
      }
    } catch (error) {
      setError('Failed to load service types');
    }
  };


  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    setError('');
    try {
      if (!formData.businessId || !formData.serviceTypeId) {
        setError('Business and service are required');
        return;
      }

      const selectedLocalDate = parseLocalDateString(selectedDate);
      if (!selectedLocalDate) {
        setError('Invalid selected date');
        return;
      }

      const serviceType = selectedServiceType || serviceTypes.find(st => st._id === formData.serviceTypeId);
      const slotDuration = serviceType?.duration || 30;

      // Availability is derived from business opening hours + closed dates.
      // Staff assignment happens later when the supplier handles the request.
      const dateParam = `${selectedLocalDate.getFullYear()}-${String(selectedLocalDate.getMonth() + 1).padStart(2, '0')}-${String(selectedLocalDate.getDate()).padStart(2, '0')}`;
      const response = await api.get(
        `/api/opening-hours/${formData.businessId}/availability/${dateParam}`,
        { params: { duration: slotDuration } }
      );

      const slots = (response.data.availableSlots || []).map(slot => ({
        startTime: slot.startTime,
        endTime: slot.endTime,
        availableTeamMembers: []
      }));

      setAvailableSlots(slots);
    } catch (error) {
      setError('Failed to load available time slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/unified-bookings', formData);

      setSuccess(true);
      if (onBookingSuccess) {
        onBookingSuccess(response.data);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const generateTimeSlots = (serviceType) => {
    if (!serviceType || !selectedDate) return [];

    if (availableSlots.length === 0) return [];

    const selectedDateObj = parseLocalDateString(selectedDate);
    if (!selectedDateObj) return [];

    const daySlots = availableSlots.filter(slot => {
      const slotDate = new Date(slot.startTime);
      slotDate.setHours(0, 0, 0, 0);
      return slotDate.getTime() === selectedDateObj.getTime();
    });

    // Get current date/time for filtering past slots
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isToday = selectedDateObj.getTime() === today.getTime();

    // Convert to the format expected by the UI and filter out past time slots
    return daySlots
      .map(slot => ({
        startTime: new Date(slot.startTime),
        endTime: new Date(slot.endTime),
        label: `${new Date(slot.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(slot.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
        availableTeamMembers: slot.availableTeamMembers || []
      }))
      .filter(slot => {
        // If it's today, only show slots that start after now
        if (isToday) {
          return slot.startTime > now;
        }
        // For future dates, show all slots
        return true;
      });
  };

  const renderServiceSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Select an Appointment
      </Typography>
      
      <Grid container spacing={2}>
        {serviceTypes.map((serviceType) => (
          <Grid item xs={12} sm={6} md={4} key={serviceType._id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                border: 1,
                borderColor: formData.serviceTypeId === serviceType._id ? 'primary.main' : 'divider',
                backgroundColor: formData.serviceTypeId === serviceType._id ? 'grey.300' : 'background.paper',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'grey.300'
                },
                transition: 'all 0.2s ease-in-out'
              }}
              onClick={() => handleInputChange('serviceTypeId', serviceType._id)}
            >
              <CardContent
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  backgroundColor: formData.serviceTypeId === serviceType._id ? 'grey.300' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'grey.300'
                  }
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {serviceType.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {serviceType.description}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Clock size={16} />
                  <Typography variant="body2">
                    {serviceType.duration} minutes
                  </Typography>
                </Box>
                {serviceType.isBlockerService && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Blocker Service" size="small" color="warning" />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!formData.serviceTypeId}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  const renderDateTimeSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Choose Date & Time
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <DatePicker
              label="Select Date"
              value={selectedDate ? new Date(selectedDate) : null}
              onChange={(newValue) => {
                if (newValue) {
                  const dateStr = formatDateToLocalString(newValue);
                  setSelectedDate(dateStr);
                } else {
                  setSelectedDate('');
                }
              }}
              minDate={new Date()}
              disablePast
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputLabelProps: { shrink: true }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            Available Time Slots
          </Typography>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {selectedDate && selectedServiceType ? (
              (() => {
                if (loadingSlots) {
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Loading available time slots...
                      </Typography>
                    </Box>
                  );
                }
                
                const timeSlots = generateTimeSlots(selectedServiceType);
                if (timeSlots.length === 0) {
                  return (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        This supplier is either fully booked or not open on this date. Please try an alternative.
                      </Typography>
                    </Alert>
                  );
                }
                return (
                  timeSlots.map((slot, index) => (
                    <Button
                      key={index}
                      variant={formData.startTime === slot.startTime.toISOString() ? 'contained' : 'outlined'}
                      onClick={() => {
                        handleInputChange('startTime', slot.startTime.toISOString());
                        handleInputChange('endTime', slot.endTime.toISOString());
                      }}
                      sx={{ m: 0.5 }}
                    >
                      {slot.label}
                    </Button>
                  ))
                );
              })()
            ) : (
              <Typography variant="body2" color="text.secondary">
                Please select a date to see available time slots
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleBack}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!formData.startTime || !formData.endTime}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  const renderDetailsForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Additional Details
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Additional Details"
            multiline
            rows={3}
            value={formData.clientNotes}
            onChange={(e) => handleInputChange('clientNotes', e.target.value)}
            placeholder="Please add any details like how many people you would like to bring, or any specific requirements"
          />
        </Grid>

      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleBack}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  const renderConfirmation = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Confirm Your Booking
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <User size={20} />
                <Typography variant="subtitle2">Service</Typography>
              </Box>
              <Typography variant="body1">{selectedServiceType?.name}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Calendar size={20} />
                <Typography variant="subtitle2">Date & Time</Typography>
              </Box>
              <Typography variant="body1">
                {new Date(formData.startTime).toLocaleDateString()} at{' '}
                {new Date(formData.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Clock size={20} />
                <Typography variant="subtitle2">Duration</Typography>
              </Box>
              <Typography variant="body1">{selectedServiceType?.duration} minutes</Typography>
            </Grid>


            {formData.clientNotes && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ChatCircleText size={20} weight="regular" />
                  <Typography variant="subtitle2">Notes</Typography>
                </Box>
                <Typography variant="body1">{formData.clientNotes}</Typography>
              </Grid>
            )}

          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleBack}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
        >
          {loading ? 'Creating Booking...' : 'Confirm Booking'}
        </Button>
      </Box>
    </Box>
  );

  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CheckCircle size={64} color="green" />
        <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
          Booking Request Sent!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your booking request has been sent to {business?.name}. 
          You will receive a notification once they respond.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/bookings')}>
            Go to bookings
          </Button>
          <Button
            variant="contained"
            onClick={onClose || (() => navigate('/bookings'))}
          >
            Close
          </Button>
        </Box>
      </Box>
    );
  }

  if (!businessId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          <Typography variant="h6">No Business Selected</Typography>
          <Typography variant="body2">
            Please select a business from the supplier dashboard to create bookings.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, textAlign: 'center' }}>
        Book Appointment
      </Typography>
      
      {business && (
        <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 4 }}>
          with {business.businessName}
        </Typography>
      )}

      <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {activeStep === 0 && renderServiceSelection()}
          {activeStep === 1 && renderDateTimeSelection()}
          {activeStep === 2 && renderDetailsForm()}
          {activeStep === 3 && renderConfirmation()}
        </CardContent>
      </Card>
    </Container>
  );
};

export default BookingForm;
